import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { User, LogOut, Plus, Trash2 } from 'lucide-react';

const API = '/api';

const GENRE_OPTIONS = [
  'Belgesel', 'Bilim Kurgu', 'Biyografik', 'Çocuk Oyunu', 'Distopya',
  'Dram', 'Gerilim', 'Kara Komedi', 'Komedi', 'Müzikal', 'Müzikli Oyun',
  'Politik', 'Polisiye', 'Romantik Komedi', 'Trajedi', 'Trajikomik'
];

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, logout } = React.useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [plays, setPlays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playSearchQuery, setPlaySearchQuery] = useState('');

  // Form states
  const [playType, setPlayType] = useState('yerli');
  const [title, setTitle] = useState('');
  const [turkishTitle, setTurkishTitle] = useState('');
  const [playwright, setPlaywright] = useState('');
  const [translator, setTranslator] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [maleCharacters, setMaleCharacters] = useState('');
  const [femaleCharacters, setFemaleCharacters] = useState('');
  const [childCharacters, setChildCharacters] = useState('');
  const [pdfFile, setPdfFile] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchPlays();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Kullanıcılar yüklenemedi');
    }
  };

  const fetchPlays = async () => {
    try {
      const response = await axios.get(`${API}/plays`);
      setPlays(response.data);
    } catch (error) {
      console.error('Error fetching plays:', error);
      toast.error('Oyunlar yüklenemedi');
    }
  };

  const handleCreatePlay = async (e) => {
    e.preventDefault();
    
    if (!title || !playwright || selectedGenres.length === 0 || !maleCharacters || !femaleCharacters || !pdfFile) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    if (playType === 'yabancı' && (!turkishTitle || !translator)) {
      toast.error('Yabancı oyunlar için Türkçe adı ve çevirmen bilgisi gereklidir');
      return;
    }

    // Validate PDF
    if (!pdfFile.name.endsWith('.pdf')) {
      toast.error('Sadece PDF dosyaları yüklenebilir');
      return;
    }

    // Check file size (75MB)
    if (pdfFile.size > 75 * 1024 * 1024) {
      toast.error('Dosya boyutu 75MB\'dan büyük olamaz');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('play_type', playType);
      formData.append('title', title);
      formData.append('turkish_title', turkishTitle);
      formData.append('playwright', playwright);
      formData.append('translator', translator);
      formData.append('genre', selectedGenres.join(', '));
      formData.append('male_characters', maleCharacters);
      formData.append('female_characters', femaleCharacters);
      formData.append('child_characters', childCharacters || 0);
      formData.append('pdf_file', pdfFile);

      await axios.post(`${API}/plays`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Oyun başarıyla oluşturuldu');
      // Clear form
      setPlayType('yerli');
      setTitle('');
      setTurkishTitle('');
      setPlaywright('');
      setTranslator('');
      setSelectedGenres([]);
      setMaleCharacters('');
      setFemaleCharacters('');
      setChildCharacters('');
      setPdfFile(null);
      fetchPlays();
    } catch (error) {
      console.error('Error creating play:', error);
      toast.error(error.response?.data?.detail || 'Oyun oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlay = async (playId) => {
    if (!window.confirm('Bu oyunu silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await axios.delete(`${API}/plays/${playId}`);
      toast.success('Oyun başarıyla silindi');
      fetchPlays();
    } catch (error) {
      console.error('Error deleting play:', error);
      toast.error('Oyun silinemedi');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole });
      toast.success('Kullanıcı rolü başarıyla güncellendi');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Kullanıcı rolü güncellenemedi');
    }
  };

  const handleUpdateTheatreType = async (userId, newTheatreType) => {
    try {
      await axios.put(`${API}/users/${userId}/theatre-type`, { theatre_type: newTheatreType });
      toast.success('Tiyatro türü başarıyla güncellendi');
      fetchUsers();
    } catch (error) {
      console.error('Error updating theatre type:', error);
      toast.error('Tiyatro türü güncellenemedi');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Kullanıcı başarıyla silindi');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = error.response?.data?.detail || 'Kullanıcı silinemedi';
      toast.error(errorMsg);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Başarıyla çıkış yapıldı');
    navigate('/');
  };

  const getRoleLabel = (role) => {
    switch(role) {
      case 'admin': return 'Yönetici';
      case 'subscribed': return 'Abone';
      case 'non-subscribed': return 'Abone Değil';
      default: return role;
    }
  };

  return (
    <div className="curtain-bg min-h-screen">
      {/* Navbar */}
      <nav className="navbar fixed top-0 left-0 right-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/dashboard')}>
            <img src="/onk-logo.png" alt="ONK Ajans" style={{ width: '175px', height: 'auto' }} />
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-amber-600 text-amber-400 hover:bg-amber-950/30"
              data-testid="back-to-dashboard-btn"
            >
              Arama Paneline Dön
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-400 hover:text-amber-400"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış
            </Button>
          </div>
        </div>
      </nav>

      {/* Gray Separator */}
      <div className="pt-32 px-6">
        <div className="max-w-7xl mx-auto h-16 bg-gray-800/50 rounded-lg"></div>
      </div>

      {/* Content */}
      <div className="px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="plays" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-gray-800/50 mb-8 mt-8" data-testid="admin-tabs">
              <TabsTrigger value="plays" className="data-[state=active]:bg-[#c50605] data-[state=active]:text-white" data-testid="plays-tab">
                Oyunları Yönet
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-[#c50605] data-[state=active]:text-white" data-testid="users-tab">
                Kullanıcıları Yönet
              </TabsTrigger>
            </TabsList>

            {/* Plays Tab */}
            <TabsContent value="plays" data-testid="plays-tab-content">
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Upload Form */}
                <div className="glass-effect rounded-xl p-6">
                  <h2 
                    className="text-3xl font-bold gold-glow mb-6"
                    data-testid="upload-form-title"
                  >
                    Yeni Oyun Yükle
                  </h2>
                  <form onSubmit={handleCreatePlay} className="space-y-4" data-testid="upload-play-form">
                    {/* Play Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-gray-300">Oyun Türü</Label>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="playType"
                            value="yerli"
                            checked={playType === 'yerli'}
                            onChange={(e) => setPlayType(e.target.value)}
                            className="w-4 h-4 text-[#c50605] bg-black/30 border-amber-800/40 focus:ring-[#c50605]"
                          />
                          <span className="text-white text-sm font-medium">Yerli</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="playType"
                            value="yabancı"
                            checked={playType === 'yabancı'}
                            onChange={(e) => setPlayType(e.target.value)}
                            className="w-4 h-4 text-[#c50605] bg-black/30 border-amber-800/40 focus:ring-[#c50605]"
                          />
                          <span className="text-white text-sm font-medium">Yabancı</span>
                        </label>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-gray-300">Oyunun Özgün Adı</Label>
                      <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Oyunun özgün adını girin"
                        className="bg-black/30 border-amber-800/40 text-white"
                        data-testid="play-title-input"
                      />
                    </div>

                    {playType === 'yabancı' && (
                      <div className="space-y-2">
                        <Label className="text-gray-300">Oyunun Türkçe Adı</Label>
                        <Input
                          value={turkishTitle}
                          onChange={(e) => setTurkishTitle(e.target.value)}
                          placeholder="Oyunun Türkçe adını girin"
                          className="bg-black/30 border-amber-800/40 text-white"
                          data-testid="play-turkish-title-input"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-gray-300">Oyun Yazarı</Label>
                      <Input
                        value={playwright}
                        onChange={(e) => setPlaywright(e.target.value)}
                        placeholder="Oyun yazarı adını girin"
                        className="bg-black/30 border-amber-800/40 text-white"
                        data-testid="play-playwright-input"
                      />
                    </div>

                    {playType === 'yabancı' && (
                      <div className="space-y-2">
                        <Label className="text-gray-300">Çevirmen</Label>
                        <Input
                          value={translator}
                          onChange={(e) => setTranslator(e.target.value)}
                          placeholder="Çevirmen adını girin"
                          className="bg-black/30 border-amber-800/40 text-white"
                          data-testid="play-translator-input"
                        />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-gray-300">Tür (Birden fazla seçebilirsiniz)</Label>
                      <div className="bg-black/30 border border-amber-800/40 rounded p-4 max-h-48 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-2">
                          {GENRE_OPTIONS.map((genre) => (
                            <label key={genre} className="flex items-center gap-2 cursor-pointer hover:bg-white/5 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={selectedGenres.includes(genre)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedGenres([...selectedGenres, genre]);
                                  } else {
                                    setSelectedGenres(selectedGenres.filter(g => g !== genre));
                                  }
                                }}
                                className="w-4 h-4 text-[#c50605] bg-black/30 border-amber-800/40 focus:ring-[#c50605] rounded"
                              />
                              <span className="text-white text-sm">{genre}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {selectedGenres.length > 0 && (
                        <div className="text-sm text-amber-400 mt-2">
                          Seçili: {selectedGenres.join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-300">Erkek Karakterler</Label>
                        <Input
                          type="number"
                          value={maleCharacters}
                          onChange={(e) => setMaleCharacters(e.target.value)}
                          min="0"
                          placeholder="0"
                          className="bg-black/30 border-amber-800/40 text-white"
                          data-testid="play-male-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Kadın Karakterler</Label>
                        <Input
                          type="number"
                          value={femaleCharacters}
                          onChange={(e) => setFemaleCharacters(e.target.value)}
                          min="0"
                          placeholder="0"
                          className="bg-black/30 border-amber-800/40 text-white"
                          data-testid="play-female-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Çocuk Karakterler</Label>
                        <Input
                          type="number"
                          value={childCharacters}
                          onChange={(e) => setChildCharacters(e.target.value)}
                          min="0"
                          placeholder="0"
                          className="bg-black/30 border-amber-800/40 text-white"
                          data-testid="play-child-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Oyun Metni (PDF)</Label>
                      <div className="flex items-center gap-4">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setPdfFile(e.target.files[0])}
                          className="text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#c50605] file:text-white hover:file:bg-[#9d0504] file:cursor-pointer"
                          data-testid="play-pdf-input"
                        />
                        {pdfFile && (
                          <span className="text-gray-300 text-sm">
                            {pdfFile.name} ({(pdfFile.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">PDF formatı, maksimum 75MB</p>
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-[#c50605] hover:bg-[#9d0504] text-white"
                      data-testid="upload-play-submit-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {loading ? 'Yükleniyor...' : 'Oyunu Yükle'}
                    </Button>
                  </form>
                </div>

                {/* Plays List */}
                <div className="glass-effect rounded-xl p-6">
                  <h2 
                    className="text-3xl font-bold gold-glow mb-6"
                    data-testid="plays-list-title"
                  >
                    Tüm Oyunlar
                  </h2>
                  
                  {/* Search Box */}
                  <div className="mb-4">
                    <Input
                      value={playSearchQuery}
                      onChange={(e) => setPlaySearchQuery(e.target.value)}
                      placeholder="Oyun adıyla ara..."
                      className="bg-black/30 border-amber-800/40 text-white"
                      data-testid="play-search-input"
                    />
                    {playSearchQuery && (
                      <p className="text-xs text-amber-400 mt-2">
                        {plays.filter(play => 
                          play.title.toLowerCase().includes(playSearchQuery.toLowerCase()) ||
                          (play.turkish_title && play.turkish_title.toLowerCase().includes(playSearchQuery.toLowerCase()))
                        ).length} oyun bulundu
                      </p>
                    )}
                  </div>

                  <div className="space-y-0 max-h-[600px] overflow-y-auto" data-testid="plays-list">
                    {plays
                      .filter(play => {
                        if (!playSearchQuery) return true;
                        const query = playSearchQuery.toLowerCase();
                        return play.title.toLowerCase().includes(query) ||
                               (play.turkish_title && play.turkish_title.toLowerCase().includes(query));
                      })
                      .map((play) => (
                      <div
                        key={play.id}
                        className="py-1 px-2 flex justify-between items-center"
                        data-testid={`play-item-${play.id}`}
                      >
                        <div className="flex-1">
                          <h3 
                            className="text-base font-bold text-amber-400"
                            data-testid={`play-item-title-${play.id}`}
                          >
                            {play.title}
                            {play.turkish_title && (
                              <span className="text-white text-sm font-normal ml-2">
                                ({play.turkish_title})
                              </span>
                            )}
                          </h3>
                        </div>
                        <Button
                          onClick={() => handleDeletePlay(play.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-950/30"
                          data-testid={`delete-play-btn-${play.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" data-testid="users-tab-content">
              <div className="glass-effect rounded-xl p-6">
                <h2 
                  className="text-3xl font-bold gold-glow mb-6"
                  data-testid="users-list-title"
                >
                  Kullanıcı Yönetimi
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-amber-800/40">
                        <th className="pb-3 text-amber-400">Ad Soyad</th>
                        <th className="pb-3 text-amber-400">E-posta</th>
                        <th className="pb-3 text-amber-400">Tiyatro</th>
                        <th className="pb-3 text-amber-400">Tür</th>
                        <th className="pb-3 text-amber-400">Okunan</th>
                        <th className="pb-3 text-amber-400">Rol</th>
                        <th className="pb-3 text-amber-400">İşlemler</th>
                        <th className="pb-3 text-amber-400">Sil</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr
                          key={u.id}
                          className="border-b border-amber-800/20"
                          data-testid={`user-item-${u.id}`}
                        >
                          <td className="py-4 text-gray-200">{u.full_name}</td>
                          <td className="py-4 text-gray-200" data-testid={`user-email-${u.id}`}>{u.email}</td>
                          <td className="py-4 text-gray-200">{u.theatre_name}</td>
                          <td className="py-4">
                            <Select
                              value={u.theatre_type}
                              onValueChange={(value) => handleUpdateTheatreType(u.id, value)}
                              data-testid={`user-theatre-select-${u.id}`}
                            >
                              <SelectTrigger className="w-[130px] bg-black/30 border-amber-800/40 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="profesyonel">Profesyonel</SelectItem>
                                <SelectItem value="amatör">Amatör</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-4 text-gray-200">
                            <span className={u.theatre_type === 'amatör' ? 'text-amber-400' : 'text-gray-400'}>
                              {u.theatre_type === 'amatör' ? `${u.read_count}/5` : '∞'}
                            </span>
                          </td>
                          <td className="py-4 text-gray-400 text-sm">{getRoleLabel(u.role)}</td>
                          <td className="py-4">
                            <Select
                              value={u.role}
                              onValueChange={(value) => handleUpdateUserRole(u.id, value)}
                              data-testid={`user-role-select-${u.id}`}
                            >
                              <SelectTrigger className="w-[130px] bg-black/30 border-amber-800/40 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="subscribed">Abone</SelectItem>
                                <SelectItem value="admin">Yönetici</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-4">
                            <Button
                              onClick={() => handleDeleteUser(u.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300 hover:bg-red-950/30"
                              data-testid={`delete-user-btn-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}